// Vanilla CSS for the loading primitives (BaseLoading, BaseLoadingWithText).
// Prefix: ld-. Aggregated into libraryStyles (injected once in App.tsx).
//
// The loader is the CatchUpper mark: the two strokes of the logo. The dark one starts drawing
// first, the light one starts later but faster, they finish together and the mark gives a small
// pop: the name, catching up. Then both fade and it starts again.
export const loadingStyles = `
  .ld-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }
  .ld-mark {
    display: block;
    overflow: visible;
  }
  .ld-stroke {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    /* pathLength="100" on each stroke makes these units percentages of the stroke at every size. */
    stroke-dasharray: 0 100;
  }
  .ld-stroke-dark  { animation: ld-dark 2.8s linear infinite; }
  .ld-stroke-light { animation: ld-light 2.8s linear infinite; }
  .ld-pop {
    transform-origin: 32.5px 52px;
    animation: ld-pop 2.8s var(--ease-glass, cubic-bezier(0.25, 0, 0, 1)) infinite;
  }
  @keyframes ld-dark {
    0%        { stroke-dasharray: 0 100;   opacity: 1; }
    55%, 82%  { stroke-dasharray: 100 100; opacity: 1; }
    96%, 100% { stroke-dasharray: 100 100; opacity: 0; }
  }
  /* Starts 18% later, finishes at the same 55% — so it visibly catches up. */
  @keyframes ld-light {
    0%, 18%   { stroke-dasharray: 0 100;   opacity: 1; }
    55%, 82%  { stroke-dasharray: 100 100; opacity: 1; }
    96%, 100% { stroke-dasharray: 100 100; opacity: 0; }
  }
  @keyframes ld-pop {
    0%, 55%   { transform: scale(1); }
    61%       { transform: scale(1.08); }
    68%, 100% { transform: scale(1); }
  }

  /* variant="wordmark": the full logo with a sheen sweeping across it — the app's boot screen. */
  .ld-word {
    position: relative;
    display: block;
  }
  .ld-word img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
  }
  .ld-word-dim { opacity: 0.28; }
  .ld-word-lit {
    -webkit-mask-image: linear-gradient(100deg, transparent 35%, #000 50%, transparent 65%);
    mask-image: linear-gradient(100deg, transparent 35%, #000 50%, transparent 65%);
    -webkit-mask-size: 250% 100%;
    mask-size: 250% 100%;
    animation: ld-sheen 2.2s cubic-bezier(0.45, 0, 0.55, 1) infinite;
  }
  @keyframes ld-sheen {
    from { -webkit-mask-position: 100% 0; mask-position: 100% 0; }
    to   { -webkit-mask-position: 0% 0;   mask-position: 0% 0; }
  }

  .ld-text-wrap {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-family: var(--font-sans);
  }
  .ld-center { text-align: center; max-width: 100%; }
  .ld-text {
    margin: 0;
    font-weight: 600;
    color: var(--brand-darker);
    line-height: 1.4;
    /* Keyed on the stage, so each new message fades in instead of the text swapping under the eye. */
    animation: ld-in var(--duration-slow, 350ms) var(--ease-glass, cubic-bezier(0.25, 0, 0, 1)) both;
  }
  /* Past 30s the copy asks the user to report the wait — toned so it reads as a notice. */
  .ld-text.ld-text-overdue { color: var(--color-warning-text); }
  .ld-txt-sm { font-size: var(--fs-sm); }
  .ld-txt-md { font-size: var(--fs-md); margin-top: 1.25rem; }
  .ld-txt-base { font-size: var(--fs-base); margin-top: 1rem; }
  .ld-txt-lg { font-size: var(--fs-lg); margin-top: 1.25rem; }
  .ld-tip {
    margin: 10px 0 0;
    max-width: 100%;
    text-align: center;
    font-size: var(--fs-md);
    font-weight: 500;
    color: var(--text-muted);
    line-height: 1.45;
    animation: ld-in 450ms var(--ease-glass, cubic-bezier(0.25, 0, 0, 1)) both;
  }
  .ld-tip b {
    color: var(--brand-darker);
    font-weight: 700;
  }
  @keyframes ld-in {
    from { opacity: 0; transform: translateY(3px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .ld-stroke-dark, .ld-stroke-light, .ld-word-lit { animation-duration: 6s; }
    .ld-pop, .ld-text, .ld-tip { animation: none; }
  }
`;
